import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/config';
import TimeSlot from './TimeSlot';
import './Calendar.css';

function Calendar({ onDateSelect, onSlotSelect, selectedDate, bookingSuccess }) {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // 生成日历网格
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // 获取当月第一天
    const firstDay = new Date(year, month, 1);
    // 获取当月最后一天
    const lastDay = new Date(year, month + 1, 0);
    
    // 计算需要显示的上个月的天数
    const firstDayOfWeek = firstDay.getDay(); // 0 是周日，1 是周一，以此类推
    
    const days = [];
    
    // 添加上个月的天数
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // 添加当月的天数
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // 添加下个月的天数以填满最后一行
    const remainingDays = 42 - days.length; // 6行7列 = 42
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    setCalendarDays(days);
  }, [currentMonth]);

  // 获取时间段
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate, bookingSuccess]);

  const fetchAvailableSlots = () => {
    setLoading(true);
    // 确保selectedDate是一个Date对象
    if (!(selectedDate instanceof Date)) {
      console.error('selectedDate is not a Date object:', selectedDate);
      setLoading(false);
      return;
    }
    
    const formattedDate = formatDate(selectedDate);
    console.log('Fetching slots for date:', formattedDate);

    fetch(`${API_BASE_URL}/api/slots?date=${formattedDate}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('Received slots:', data);
        setAvailableSlots(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching slots:', error);
        setAvailableSlots([]);
        setLoading(false);
      });
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleDateClick = (day) => {
    if (day.isCurrentMonth) {
      onDateSelect(day.date);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    onSlotSelect(slot);
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button onClick={prevMonth}>&lt;</button>
        <h2>{currentMonth.getFullYear()}年{currentMonth.getMonth() + 1}月</h2>
        <button onClick={nextMonth}>&gt;</button>
      </div>

      <div className="calendar-days">
        <div className="weekdays">
          <div>日</div>
          <div>一</div>
          <div>二</div>
          <div>三</div>
          <div>四</div>
          <div>五</div>
          <div>六</div>
        </div>

        <div className="days-grid">
          {calendarDays.map((dayInfo, index) => {
            const isToday = new Date().toDateString() === dayInfo.date.toDateString();
            const isSelected = selectedDate && selectedDate.toDateString() === dayInfo.date.toDateString();
            const isPast = dayInfo.date < new Date(new Date().setHours(0, 0, 0, 0));

            return (
              <div
                key={index}
                className={`day 
                  ${isToday ? 'today' : ''} 
                  ${isSelected ? 'selected' : ''} 
                  ${isPast ? 'past' : ''} 
                  ${!dayInfo.isCurrentMonth ? 'other-month' : ''}`}
                onClick={() => !isPast && handleDateClick(dayInfo)}
              >
                {dayInfo.date.getDate()}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="time-slots">
          <h3>{formatDate(selectedDate)} 可预约时间</h3>
          {loading ? (
            <p>加载中...</p>
          ) : (
            <div className="slots-grid">
              {availableSlots.length > 0 ? (
                availableSlots.map((slot, index) => (
                  <TimeSlot
                    key={index}
                    slot={slot}
                    onSelect={handleSlotSelect}
                    isSelected={selectedSlot && selectedSlot.startTime === slot.startTime && selectedSlot.endTime === slot.endTime}
                  />
                ))
              ) : (
                <p>当天没有可预约的时间段</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Calendar;